/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState, ReactElement } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";

import { PrimaryButton, SearchBox, Stack } from "@fluentui/react";

import * as AzureBenefitRequestsActions from "Dataflows/AzureBenefitRequests/AzureBenefitRequestsAction";

import AzureRequestsList from "Components/AzureRequestsList/AzureRequestsList";
import ListCounts from "Components/Common/ListCounts/ListCounts";
import { Title } from "Components/Common/Results/Result.styles";
import {
  azureBenefitRequestsFilterFields,
  defaultFilterValues,
  defaultListingPageSize,
} from "Constants/AzureBenefitConstants";
import {
  buttonStackTokens,
  containerStackStyles,
  filterButtonStyles,
  listStackStyles,
  searchBoxStyles,
  PaddedFlexRow,
} from "Containers/Common/CommonContainer.styles";
import SideBarContainer from "Containers/SideBarContainer/SideBarContainer";
import {
  getAzureBenefitRequests,
  getAzureBenefitRequestsFilterValues,
  getCurrentPage,
  getFilteredCount,
  getLoadAzureBenefitRequestsError,
  getPageSize,
  getSearchText,
  isLoadingAzureBenefitRequests,
} from "Dataflows/AzureBenefitRequests/AzureBenefitRequestsSelector";
import { IFilterValue } from "Interfaces/IFilterValue";

/**
 * The Azure Benefit Requests Container.
 */
const AzureBenefitRequestsContainer: React.FunctionComponent = (): ReactElement => {
  const dispatch = useDispatch();
  const { tenant } = useParams<{ tenant: string }>();

  const requests = useSelector(getAzureBenefitRequests);
  const currentPage = useSelector(getCurrentPage);
  const filteredCount = useSelector(getFilteredCount);
  const isLoading = useSelector(isLoadingAzureBenefitRequests);
  const loadAzureBenefitRequestsError = useSelector(getLoadAzureBenefitRequestsError);
  const pageSize = useSelector(getPageSize);
  const searchText = useSelector(getSearchText);
  const azureBenefitRequestsFilterValues = useSelector(getAzureBenefitRequestsFilterValues);

  const [searchTextState, setSearchTextState] = useState(searchText);

  const handleSearch = (
    searchValue: string,
    selectedAzureBenefitRequestsFilterValues: IFilterValue[],
    page: number,
    pageSizeLocal: number
  ): void => {
    dispatch(
      AzureBenefitRequestsActions.searchAzureBenefitRequests(
        searchValue,
        selectedAzureBenefitRequestsFilterValues,
        page,
        pageSizeLocal,
        tenant
      )
    );
  };

  /**
   * Call to reset the filter values.
   * @method
   */
  const resetFilterValues = (): void => {
    dispatch(AzureBenefitRequestsActions.clearAzureBenefitRequestsFilterValues());

    setSearchTextState("");
    handleSearch("", defaultFilterValues, 1, defaultListingPageSize);
  };

  useEffect(() => {
    handleSearch(searchTextState, azureBenefitRequestsFilterValues, currentPage, pageSize);
  }, [currentPage, pageSize, tenant]);

  const onClear = (): void => {
    handleSearch("", azureBenefitRequestsFilterValues, 1, pageSize);
  };

  return (
    <Stack horizontal styles={containerStackStyles}>
      <Stack.Item disableShrink>
        <SideBarContainer
          addFilterValue={(filterValue) =>
            dispatch(AzureBenefitRequestsActions.addAzureBenefitRequestsFilterValue(filterValue))
          }
          removeFilterValue={(filterValue) =>
            dispatch(AzureBenefitRequestsActions.removeAzureBenefitRequestsFilterValue(filterValue))
          }
          updateFilterValue={(filterValue) =>
            dispatch(AzureBenefitRequestsActions.updateAzureBenefitRequestsFilterValue(filterValue))
          }
          selectedFilterValues={azureBenefitRequestsFilterValues}
          filterFields={azureBenefitRequestsFilterFields}
        />
        <Stack horizontal tokens={buttonStackTokens}>
          <PrimaryButton
            styles={filterButtonStyles()}
            text="Apply"
            onClick={(): void =>
              handleSearch(searchTextState, azureBenefitRequestsFilterValues, 1, pageSize)
            }
          />
          <PrimaryButton styles={filterButtonStyles()} text="Reset" onClick={resetFilterValues} />
        </Stack>
      </Stack.Item>
      <Stack.Item grow styles={listStackStyles()}>
        <PaddedFlexRow>
          <Title>Azure Requests</Title>
          <ListCounts total={filteredCount} loading={isLoading} />
        </PaddedFlexRow>
        <SearchBox
          placeholder="Search requests"
          value={searchTextState}
          onSearch={(searchValue: string): void =>
            handleSearch(searchValue, azureBenefitRequestsFilterValues, 1, pageSize)
          }
          onChange={(event?: React.ChangeEvent<HTMLInputElement>, newValue?: string): void =>
            setSearchTextState(newValue || "")
          }
          onClear={onClear}
          styles={searchBoxStyles}
        />
        <AzureRequestsList
          data={requests}
          filteredCount={filteredCount}
          pageSize={pageSize}
          isLoadingAzureRequests={isLoading}
          currentPage={currentPage}
          loadAzureRequestsError={loadAzureBenefitRequestsError}
          tenant={tenant}
        />
      </Stack.Item>
    </Stack>
  );
};

export default AzureBenefitRequestsContainer;
